import * as B from '@blueprintjs/core';
import {useQueryClient} from '@tanstack/react-query';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {exploreRunsQueryOptions} from 'app/components/AgentExplorer/hooks';
import {isRunInFlight} from 'app/components/AgentExplorer/types';
import {usePushNotification} from 'app/components/Notification';
import {AGENT_RUN_DETAILS_QUERY_KEY} from 'app/pages/Agent/hooks';

import {ApiRecentRun, pendingRunUrl, usePendingAgentRuns} from './usePendingAgentRuns';

/**
 * Toasts when a tracked run in the polled recent-runs data reaches a terminal
 * status, and once per run plan batch when the batch itself settles; the
 * provider owns the poll and the registry pruning.
 */
const PendingAgentRunsMonitor: React.FC = () => {
  const {pendingRuns, removePendingRun, pendingBulkRuns, removePendingBulkRun, recentRuns} =
    usePendingAgentRuns();
  const pushNotification = usePushNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!recentRuns) {
      return;
    }
    const runsById = new Map(recentRuns.map((run) => [run.id, run]));

    for (const entry of pendingRuns.values()) {
      const run = runsById.get(entry.runId);
      if (!run || isRunInFlight(run.status)) {
        continue;
      }

      removePendingRun(entry.runId);
      // The sidebar renders from the per-feature explore-runs cache, which nothing
      // else refreshes on completion; without this, "View" lands on the stale
      // in-progress state.
      queryClient.invalidateQueries({queryKey: exploreRunsQueryOptions(run.featureId).queryKey});

      // A batch reports its own completion, so its properties' runs stay quiet.
      if (run.agentWorkflowRunId != null) {
        continue;
      }

      const failed = run.status === 'failed';
      const runUrl = pendingRunUrl(entry, run);
      pushNotification({
        message: `Agent run ${failed ? 'failed' : 'completed'}${
          run.featureName ? ` for ${run.featureName}` : ''
        }.`,
        autoHideDuration: 0,
        // Completions in the same poll tick each get their own toast.
        shouldClearPrevToast: false,
        options: {
          intent: failed ? B.Intent.DANGER : B.Intent.SUCCESS,
          icon: 'clean',
          ...(runUrl ? {action: {text: 'View', onClick: () => navigate(runUrl)}} : {}),
        },
      });
    }
  }, [pendingRuns, recentRuns, removePendingRun, pushNotification, navigate, queryClient]);

  React.useEffect(() => {
    if (!recentRuns) {
      return;
    }
    // A batch's own status and title ride on every one of its runs, so any of its rows stands in
    // for it here.
    const rowByBatchId = new Map<number, ApiRecentRun>();
    for (const run of recentRuns) {
      if (run.agentWorkflowRunId != null) {
        rowByBatchId.set(run.agentWorkflowRunId, run);
      }
    }

    for (const entry of pendingBulkRuns.values()) {
      const batchRow = rowByBatchId.get(entry.workflowRunId);
      const status = batchRow?.agentWorkflowRunStatus;
      // A batch with no row yet says nothing about whether it finished, so it stays tracked.
      if (!status || isRunInFlight(status)) {
        continue;
      }

      removePendingBulkRun(entry.workflowRunId);
      // Cancelling or deleting a run plan settles its batch, which nobody needs telling about.
      if (status === 'cancelled') {
        continue;
      }

      // The plan run page renders from a cache nothing else refreshes on completion, and this toast
      // links straight to it. Its key carries route params, which are strings, so match by prefix.
      queryClient.invalidateQueries({queryKey: [AGENT_RUN_DETAILS_QUERY_KEY]});

      // One failed property fails the whole batch, so the wording stops short of a failed batch.
      const failed = status === 'failed';
      const title = batchRow?.agentWorkflowTitle ?? entry.title;
      const url = entry.url;
      pushNotification({
        message: `Run plan${title ? ` "${title}"` : ''} completed${failed ? ' with errors' : ''}.`,
        autoHideDuration: 0,
        shouldClearPrevToast: false,
        options: {
          intent: failed ? B.Intent.DANGER : B.Intent.SUCCESS,
          icon: 'clean',
          ...(url ? {action: {text: 'View', onClick: () => navigate(url)}} : {}),
        },
      });
    }
  }, [pendingBulkRuns, recentRuns, removePendingBulkRun, pushNotification, navigate, queryClient]);

  return null;
};

export default PendingAgentRunsMonitor;
